import type { MutableRefObject, ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { Braces, ChartNoAxesColumn, Cloud, CloudOff, Settings2, Trash2 } from "lucide-react";
import { useStore } from "../../../helpers";
import type { ChartPaneMode } from "../../../stores/tars/tars-analytics-store";
import { FACET_WS } from "../notebook-details/analytics/facet-workspace-theme";
import { facetViewJsonTabClass, FACET_VIEW_JSON_TABLIST_CLASS } from "./FacetViewJsonToggle";
import TARSChartCopyImageToolbar from "./TARSChartCopyImageToolbar";
import TARSChartPublishToolbar from "./TARSChartPublishToolbar";
import ChartConfigFormPanel from "./chart-config-form/ChartConfigFormPanel";
import FacetResultSourceToggle, {
    type FacetResultDataSource,
} from "../notebook-details/analytics/FacetResultSourceToggle";
import {
    TARS_CHART_NOT_PUSHED_STATUS_LABEL,
    TARS_CHART_PUBLISH_AGAIN_BUTTON_LABEL,
    TARS_CHART_PUBLISH_BUTTON_LABEL,
    TARS_CHART_PUSHED_STATUS_LABEL,
} from "../tars-constants";

const CHART_SECTION_TITLE_ID = "facet-chart-output-title";

interface FacetChartModePanelProps {
    children: ReactNode;
    onReset: () => void;
    onSave: () => void;
    onDelete: () => void;
    chartCopyRef?: MutableRefObject<(() => Promise<void>) | null>;
    onPublishChart?: () => Promise<void>;
    publishDisabled?: boolean;
    /** Whether the active chart already has cloud image metadata. */
    chartPushed?: boolean;
    chartEntry?: Record<string, unknown> | null;
    onSaveConfig?: (updatedEntry: Record<string, unknown>) => void;
    hasNormalizationResult?: boolean;
    resultDataSource?: FacetResultDataSource;
    onResultDataSource?: (source: FacetResultDataSource) => void;
}

function FacetChartModePanelInner({
    children,
    onReset,
    onSave,
    onDelete,
    chartCopyRef,
    onPublishChart,
    publishDisabled = false,
    chartPushed = false,
    chartEntry,
    onSaveConfig,
    hasNormalizationResult = false,
    resultDataSource = "facet",
    onResultDataSource,
}: FacetChartModePanelProps) {
    const { tarsAnalyticsStore } = useStore();
    const { chartPane, chartDraft } = tarsAnalyticsStore;

    const handlePaneChange = (mode: ChartPaneMode) => {
        tarsAnalyticsStore.setChartPane(mode);
    };

    return (
        <section
            className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
            aria-labelledby={CHART_SECTION_TITLE_ID}
        >
            <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 bg-white/80 px-4 py-3 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <h3 id={CHART_SECTION_TITLE_ID} className="text-[12px] font-medium text-slate-500">
                        Data output
                    </h3>
                    {onPublishChart ? (
                        <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                chartPushed
                                    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                    : "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
                            }`}
                        >
                            {chartPushed ? (
                                <Cloud size={12} strokeWidth={2.5} aria-hidden />
                            ) : (
                                <CloudOff size={12} strokeWidth={2.5} aria-hidden />
                            )}
                            {chartPushed
                                ? TARS_CHART_PUSHED_STATUS_LABEL
                                : TARS_CHART_NOT_PUSHED_STATUS_LABEL}
                        </span>
                    ) : null}
                    {hasNormalizationResult && chartPane === "view" && onResultDataSource ? (
                        <FacetResultSourceToggle
                            value={resultDataSource}
                            onChange={onResultDataSource}
                        />
                    ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div
                        className={FACET_VIEW_JSON_TABLIST_CLASS}
                        style={{ background: FACET_WS.segmentTrack }}
                        role="tablist"
                        aria-label="Chart view, JSON or config"
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={chartPane === "view"}
                            onClick={() => handlePaneChange("view")}
                            className={facetViewJsonTabClass(chartPane === "view")}
                        >
                            <ChartNoAxesColumn size={14} strokeWidth={2} aria-hidden />
                            Chart
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={chartPane === "json"}
                            onClick={() => handlePaneChange("json")}
                            className={facetViewJsonTabClass(chartPane === "json")}
                        >
                            <Braces size={14} strokeWidth={2} aria-hidden />
                            JSON
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={chartPane === "config"}
                            onClick={() => handlePaneChange("config")}
                            className={facetViewJsonTabClass(chartPane === "config")}
                        >
                            <Settings2 size={14} strokeWidth={2} aria-hidden />
                            Config
                        </button>
                    </div>
                    {chartPane === "view" && chartCopyRef ? (
                        <TARSChartCopyImageToolbar copyRef={chartCopyRef} />
                    ) : null}
                    {chartPane === "view" && onPublishChart ? (
                        <TARSChartPublishToolbar
                            onPublish={onPublishChart}
                            disabled={publishDisabled}
                            buttonLabel={
                                chartPushed
                                    ? TARS_CHART_PUBLISH_AGAIN_BUTTON_LABEL
                                    : TARS_CHART_PUBLISH_BUTTON_LABEL
                            }
                        />
                    ) : null}
                    <button
                        type="button"
                        onClick={onDelete}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-red-700 shadow-sm transition hover:bg-red-50"
                        aria-label="Delete chart"
                    >
                        <Trash2 size={14} strokeWidth={2} aria-hidden />
                        Delete
                    </button>
                </div>
            </header>
            {chartPane === "view" ? (
                <figure className="m-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-auto px-2 pb-6 pt-2">
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200/90 bg-white shadow-sm">
                        {children}
                    </div>
                </figure>
            ) : chartPane === "config" ? (
                <ChartConfigFormPanel
                    chartEntry={chartEntry ?? null}
                    onSave={(updated) => onSaveConfig?.(updated)}
                />
            ) : (
                <>
                    <div className="flex min-h-0 flex-1 flex-col bg-slate-50/50">
                        <textarea
                            value={chartDraft}
                            onChange={(e) => tarsAnalyticsStore.setChartDraft(e.target.value)}
                            spellCheck={false}
                            placeholder="{}"
                            aria-label="Chart JSON"
                            className="min-h-0 flex-1 resize-none border-0 bg-transparent px-4 py-4 font-mono text-[12px] leading-relaxed text-slate-800 outline-none ring-0 placeholder:text-slate-400"
                        />
                    </div>
                    <footer className="flex items-center justify-end gap-2 border-t border-slate-200/80 bg-white/90 px-4 py-2.5">
                        <button
                            type="button"
                            onClick={onReset}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={onSave}
                            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                        >
                            Save chart JSON
                        </button>
                    </footer>
                </>
            )}
        </section>
    );
}

export default observer(FacetChartModePanelInner);
